import { Link } from "@tanstack/react-router";
import { useOriginLinkSearch } from "@/components/shared/origin-context";

interface OriginLinkProps {
  /**
   * Target route path. Typed as a plain string (like `BackLink`) so a caller can
   * point it at any route without fighting per-route search typing.
   */
  to: string;
  params?: Record<string, string>;
  /** Extra search to merge alongside the injected `origin`. */
  search?: Record<string, string>;
  className?: string;
  title?: string;
  "aria-label"?: string;
  /** e.g. `(e) => e.stopPropagation()` on a link nested inside a clickable row. */
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  children: React.ReactNode;
}

/**
 * A `<Link>` that stamps the CURRENT page as the origin of wherever it goes, so
 * the destination shows a "← Back to <here>" breadcrumb/back-link. Use it for
 * any contextual cross-page link (not nav chrome). See `origin-context.tsx`.
 */
export function OriginLink({
  to,
  params,
  search,
  className,
  title,
  "aria-label": ariaLabel,
  onClick,
  children,
}: OriginLinkProps) {
  const origin = useOriginLinkSearch();
  return (
    <Link
      to={to}
      params={params}
      search={{ ...search, ...origin }}
      className={className}
      title={title}
      aria-label={ariaLabel}
      onClick={onClick}
    >
      {children}
    </Link>
  );
}
